<template>
  <div>
    <GameBoard ref="gameBoard"
               :size="$store.state.isMobile ? 0 : boardSize"
               :playMode="playMode"
               :state="state"
               @action="onAction"
               />
    <div v-if="selected && playMode === 'button'" class="placement">
      <ActionButton :key="placement.key" :label="t('button-place-stone')"
                    :action="placement.action" class="put" />
    </div>
    <component v-for="(section, i) in notificationSections" :is="section" :key="i"
               :state="ownNotifiable" :waiting="waitingForOpponent" />

    <div v-if="!selected && !messageBoxDef" class="tools">
      <button class="estimate btn-secondary" @click="estimate">{{ t('button-estimate') }}</button>
      <!-- 聊天, right of 形势判断, which stays in the middle (SPEC §5.6). -->
      <ChatButton v-if="chat" class="chat-btn" :pressed="chat.shown" @click="$emit('chat-toggle')"/>
    </div>
    <!-- The AI hasn't answered 15 s into its turn (plan 2026-10-28-ai-games.md §4.2). -->
    <p v-if="aiSlow && !messageBoxDef" class="ai-slow">{{ t('ai-game-slow') }}</p>
    <PlayModeToggle v-if="!selected && !messageBoxDef"/>
    <span>{{estimateText}}</span>
    <MessageBox v-model="messageBoxDef"/>
  </div>
</template>

<script>
import Game from '@/game/Game';
import { Stone } from '@/game/Board';
import GameBoard, { State, InfluenceChain, InputValidator, Action } from './GameBoard';
import { SimpleStoneChain } from './GameBoard/StoneChain';
import MessageBox from './MessageBox';
import PlayModeToggle from './PlayModeToggle';
import { t } from '@/texts';
import Session from '@/session/Session';
import ActionButton from './ActionButton';
import ChatButton from './ChatButton';
import extensions from '@/extensions';
import api from '@/api/api';
import { desktopBoardSize } from '@/utils';
import text, { countResultText } from '@/text';

// How long the AI may take before the page says it may be offline.
const AI_SLOW_MS = 15000;

class PlayInputValidator extends InputValidator {
  constructor(game, player) {
    super();
    this.playable = false;
    this.game = game;
    this.player = player;
  }

  get inputType() {
    if(this.playable && this.game &&
      this.game.status === 'in-progress' &&
      (this.player === this.game.nextPlayer || this.player === Stone.BOTH)) {
      return this.game.nextPlayer;
    } else {
      return null;
    }
  }

  validate(point) {
    return this.game && this.game.canPlay(point);
  }
}

export default {
  name: 'GameSession',

  props: {
    game: { type: Game, required: true },
    player: { type: Symbol },
    // The game's chat (chat.js), for the 聊天 button.
    chat: { type: Object, default: null },
  },

  data() {
    return {
      session: null,
      notifiable: {},
      notificationSections: extensions.notificationSections,
      selected: null,
      messageBoxDef: null,
      input: null,
      state: null,
      aiSlow: false,
      aiSlowTimer: null,
    }
  },

  watch: {
    'session.revision'() { this.selected = null; },
    'input.inputType'() { this.selected = null; },
    // The chat waits while a dialog (e.g. the count) is shown (SPEC §5.6).
    messageBoxDef(v) { this.$emit('dialog', !!v); },
    // The slow line counts from the moment the AI's turn began on this page.
    aiWaiting: {
      immediate: true,
      handler(waiting) {
        clearTimeout(this.aiSlowTimer);
        this.aiSlow = false;
        if(waiting) this.aiSlowTimer = setTimeout(() => { this.aiSlow = true; }, AI_SLOW_MS);
      },
    },
  },

  computed: {
    // On a desktop the board fits the window's height with the nav bar, the game line and the
    // buttons below it (SPEC §7.1).
    boardSize() {
      return desktopBoardSize(230);
    },

    turnKey() { return `${this.game.id}:${this.session.revision}:${this.game.moves.length}`; },
    placement() {
      const point = this.selected && { ...this.selected };
      const revision = this.session.revision, count = this.game.moves.length;
      return {
        key: `${this.turnKey}:${point && point.x}:${point && point.y}`,
        action: () => this.buttonPut(point, revision, count),
      };
    },
    opponent() {
      const u = this.$store.state.user;
      return u && [Stone.BLACK, Stone.WHITE].includes(this.player) && this.game.players.find(p => p.id !== u.id);
    },
    canNotify() { return !!this.opponent && !!(this.notifiable[this.opponent.id] || {}).possible; },
    waitingForOpponent() {
      return !!this.opponent && this.game.status === 'in-progress' && !this.session.isCounting &&
        this.game.nextPlayer !== this.player && this.game.moves.length === this.session.confirmedMoves;
    },
    // Waiting for the AI's move, in a game against it.
    aiWaiting() { return !!this.game.aiLevel && !!this.session && this.waitingForOpponent; },
    ownNotifiable() { const u = this.$store.state.user; return u ? this.notifiable[u.id] || {} : {}; },
    playMode() {
      return (this.$store.state.ui && this.$store.state.ui.playMode) || 'button';
    },

    estimateText() {
      if(this.state.infChain && this.state.infChain.index === this.cursor)
        return `黑:${this.state.infChain.black}, 白:${this.state.infChain.white}`;
      else
        return "";
    },
  },

  components: {
    GameBoard,
    MessageBox,
    PlayModeToggle,
    ActionButton,
    ChatButton,
  },

  timers: {
    keepalive: { time: 3000, autostart: true, repeat: true }
  },

  beforeDestroy() {
    clearTimeout(this.aiSlowTimer);
    if (this.session) this.session.close();
  },

  methods: {
    t,
    // Asks first, like resigning (SPEC §5.3).
    async takeBackForOpponent() {
      const r = await this.messageBox(t('take-back-confirm'), t('button-confirm'), t('dialog-cancel'));
      if(r === 'ok') this.session.takeBackForOpponent();
    },

    resign() {
      this.session.resign();
    },

    count() {
      this.session.startCount();
    },

    keepalive() {
      this.session.keepalive();
    },

    buttonPut(point, revision, count) {
      if (!point || !this.selected || point.x !== this.selected.x || point.y !== this.selected.y ||
          this.session.disposed || this.session.revision !== revision || this.game.moves.length !== count ||
          this.session.confirmedMoves !== count || this.input.inputType === null) return;
      this.put(point);
      this.selected = null;
    },

    put(point) {
      console.log(`put(${point})`);
      this.session.expectNotice = this.canNotify;
      const ok = this.game.play(point);
      if(!ok) { this.session.expectNotice = false; this.$emit('message', this.game.unplayableReason); }
    },

    messageBox(txt, okText=null, cancelText=null, detail=null) {
      return new Promise(resolve => {
        this.messageBoxDef = {
          text: txt,
          detail,
          okText,
          cancelText,
          ok: okText ? () => { resolve('ok'); } : null,
          cancel: cancelText ? () => { resolve('cancel'); } : null,
        }
      });
    },

    async estimate() {
      if(this.state.infChain !== null)
        this.state.infChain = null;
      else {
        const r = await api.post(api.ESTIMATES, {
          game: this.game.id,
          moveIndex: this.game.moves.length
        });

        if(r.data.moveIndex === this.game.moves.length)
          this.state.infChain = new InfluenceChain(this.game, this.cursor, r.data.countResult.pointMap);
      }
    },

    onAction(action) {
      switch(action.type) {
        case Action.Type.PUT:
          this.state.infChain = null;
          if(action.point) this.put(action.point);
          break;

        case Action.Type.SELECT:
          this.state.infChain = null;
          this.selected = action.point;
          break;
      }
    },
  },

  created() {
    this.input = new PlayInputValidator(this.game, this.player);
    this.state = new State(this.game.grid, new SimpleStoneChain(this.game.moves), null,  null, this.input);

    const self = this;
    this.game.addWatcher({
      // eslint-disable-next-line no-unused-vars
      onMove(move) {
        self.state.stoneChain.index = self.game.moves.length;
        self.$audio.play('stone');
      },

      // eslint-disable-next-line no-unused-vars
      onTakeBack(move) {
        self.state.stoneChain.index = self.game.moves.length;
      },
    });

    this.session = new Session(this.game, this.player, {
      afterDraw: () => self.$nextTick().then(() => new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)))),
      onNotifiable(msg) { self.$set(self.notifiable, msg.user.id, msg); },
      onPresence(msg) { self.$emit('presence', msg); },
      onChat(m) { self.$emit('chat', m); },
      onJoined() { self.$emit('joined'); },
      onNoticeProblem(msg) {
        const tags = { 'not-enabled': 'notify-opponent-not-sent', failed: 'notify-opponent-failed', unconfirmed: 'notify-opponent-unconfirmed' };
        if (self.canNotify && tags[msg.status]) self.$emit('message', t(tags[msg.status]), 'warn');
      },
      onError(err) {
        self.$emit('message', err, 'warn');
      },

      onPlay() {
        self.input.playable = true;
        self.messageBoxDef = null;
        self.state.infChain = null;
      },

      onCompleted() {
        self.input.playable = false;
        self.$emit('completed');
      },

      async onCountRequest(stone) {
        self.input.playable = false;
        // In a game against the AI, the other colour's request is the AI's (its pass).
        const asked = self.game.aiLevel && stone !== self.player ? t('ai-count-request') : `${text.stoneLong(stone)}请求数子`;
        const r = await self.messageBox(asked, '同意', '不同意');
        return r === 'ok';
      },

      async onCountRequestSent(stone) {
        self.input.playable = false;
        await self.messageBox(`已请求数子, 等待${text.stoneLong(Game.invert(stone))}同意`, '取消');
      },

      async onCountResult(result) {
        self.input.playable = false;
        self.state.infChain = new InfluenceChain(self.game, self.cursor, result.pointMap);
        // The result being confirmed, in 子, with the winner by the rule of SPEC §5.5.
        const score = countResultText(self.game, result);
        const r = await self.messageBox(t('count-result-confirm'), t('button-confirm'), t('dialog-cancel'), score);
        self.state.infChain = null;
        return r === 'ok';
      },

      onCountConfirmSent(stone) {
        self.input.playable = false;
        self.messageBox(`已确认数子结果, 等待${text.stoneLong(Game.invert(stone))}确认`);
      },

      async confirm(txt) {
        const r = await self.messageBox(txt, '确认', '取消');
        return r === 'ok';
      },
    });
  },
}
</script>

<style scoped>
/* The place-stone button is the page's main button (wood brown); inside WeChat the native button
   copies its colour, radius and size (SPEC §5.1). */
.placement .put {
  -webkit-appearance: none;
  appearance: none;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 300px;
  max-width: 100%;
  height: 46px;
  margin-left: auto;
  margin-right: auto;
  padding: 0;
  border: none;
  border-radius: 10px;
  background: #8b5e34;
  color: #fff;
  font-size: 17px;
  line-height: normal;
  text-align: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, .25);
  cursor: pointer;
}

.estimate {
  margin-top: 10px;
  width: 200px;
}

.placement { margin: 10px auto; max-width: 300px; }

.tools { position: relative; }

.ai-slow {
  color: #888;
  font-size: 15px;
  margin: 8px auto;
  padding: 0 16px;
  max-width: 420px;
}
.tools .chat-btn {
  position: absolute;
  left: calc(50% + 112px);
  top: 7px;
}
</style>
